import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * The marquee frame: one `border-image` nine-slice plus a chase of bulbs laid
 * around its perimeter. Values are the ones the prototype settled on — sprite
 * scale 1x, 12px bulb, 15px pitch, 5s lap, 33% lit arc.
 *
 * Bulbs are fitted `floor(edgeLength / pitch)` per edge with the remainder
 * absorbed into the gaps, then each gets `animation-delay: -(index/total) x lap`
 * — a true constant-speed chase, so the wave neither slows nor bunches at the
 * corners. Only `opacity` animates, so it stays on the compositor.
 */
const SPRITE = { rail: 18, corner: 20, pitch: 15 };
const CYCLE_S = 5;

/** Distribute points along a segment from its start, excluding the end — the next edge owns that. */
function edgePoints(x1: number, y1: number, x2: number, y2: number): [number, number][] {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const n = Math.max(1, Math.floor(len / SPRITE.pitch));
  const pts: [number, number][] = [];
  for (let k = 0; k < n; k += 1) {
    const t = k / n;
    pts.push([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t]);
  }
  return pts;
}

export function BulbFrame({ className = '', children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pts, setPts] = useState<[number, number][]>([]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // clientWidth/Height measure the PADDING box, so the offset out to the
      // rail centreline is negative — the bulbs sit on top of the border.
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const inset = SPRITE.rail / 2 - SPRITE.corner;
      const [L, R, T, B] = [inset, w - inset, inset, h - inset];
      setPts([
        ...edgePoints(L, T, R, T),
        ...edgePoints(R, T, R, B),
        ...edgePoints(R, B, L, B),
        ...edgePoints(L, B, L, T),
      ]);
    };

    measure();
    // Bulbs are absolutely positioned, so adding them cannot resize the box and
    // re-trigger this.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className={`bulbed ${className}`.trim()}>
      {children}
      {pts.map(([x, y], i) => (
        <span
          key={i}
          className="bulb"
          style={
            {
              left: x,
              top: y,
              ['--d' as string]: `${(-(i / pts.length) * CYCLE_S).toFixed(4)}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
